import { CurrencyPipe, formatDate } from '@angular/common';
import { Component, OnInit, computed, inject, input, signal } from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridOptions } from 'ag-grid-community';
import { min, sortBy, startCase, sumBy, uniq } from 'lodash';
import { DateTime, Interval } from 'luxon';
import { forkJoin } from 'rxjs';
import { defaultTableDefOptions } from '../../../helpers';
import { BranchService } from '../../../service/branch.service';
import { LocationService } from '../../../service/location.service';
import { SubscriptionService } from '../../../service/subscription.service';
import { Branch, BundleTransaction, ChangeEntry, LocationResponse, StripePlan } from '../../../types';
import { LocationChangelogsService } from '../../../service/location-changelogs.service';
import { LocationChangelog } from '../../../types/location-changelog';

@Component({
  selector: 'app-branch-billing',
  templateUrl: './branch-billing.component.html',
  styleUrls: ['./branch-billing.component.scss'],
  standalone: false,
})
export class BranchBillingComponent implements OnInit {
  private locationService = inject(LocationService);
  private locationChangelogsService = inject(LocationChangelogsService);
  private subscriptionService = inject(SubscriptionService);
  private branchService = inject(BranchService);
  private currencyPipe = inject(CurrencyPipe);

  public branchId = input<number>();
  public branchData = input<Branch>();

  public isLoading = signal<boolean>(false);
  public branch = signal<Branch | undefined>(undefined);
  public monthSelection = signal<Date>(new Date());

  stripeProductPrices = signal<StripePlan[]>([]);
  locationHistory = signal<Record<number, ChangeEntry[]>>({});
  filteredLocations = signal<LocationResponse[]>([]);
  filteredTransactions = signal<BundleTransaction[]>([]);

  public subscriptionFileName = computed(() => this.generateFileName('subscriptions'));
  public hardwareFileName = computed(() => this.generateFileName('hardware'));

  private practicalMin = computed(() => DateTime.fromJSDate(this.monthSelection()).startOf('month'));
  private practicalMax = computed(() => DateTime.fromJSDate(this.monthSelection()).endOf('month'));

  public subscriptionList = computed(() => {
    const min = this.practicalMin();
    const max = this.practicalMax();

    const locationHistory = this.locationHistory();
    const filteredLocations = this.filteredLocations();

    const locationData = filteredLocations.flatMap((location) => locationHistory[location.id] ?? []);

    return locationData
      .map((entry) => {
        const startDate = DateTime.fromJSDate(entry.startDate);
        const isStartWithinMonth = Interval.fromDateTimes(min, max).contains(startDate);

        let endDate = entry.endDate ? DateTime.fromJSDate(entry.endDate) : max;
        const isEndWithinMonth = Interval.fromDateTimes(min, max).contains(endDate);
        endDate = isEndWithinMonth ? endDate : max;

        if (isStartWithinMonth || isEndWithinMonth) {
          return {
            ...entry,
            startDate: isStartWithinMonth ? startDate.toJSDate() : min.toJSDate(),
            endDate: endDate.toJSDate(),
          };
        }
      })
      .filter(Boolean);
  });

  public subscriptionTableItems = computed(() =>
    this.subscriptionList().map((entry) => ({
      ...entry,
      priceName: startCase(this.getStripePriceData(entry.priceId).name),
      startDate: formatDate(entry.startDate, 'mediumDate', 'en-US'),
      endDate: formatDate(entry.endDate, 'mediumDate', 'en-US') || 'Current',
      unitPrice: this.getDisplayUnitAmount(entry.priceId),
      usage: this.getUsageForEntry(entry),
      amount: this.currencyPipe.transform(this.getAmountDueForEntry(entry), 'USD'),
    })),
  );

  public subscriptionTotal = computed(() => {
    const history = this.subscriptionList();
    if (!history || history.length === 0) return 0;

    return sumBy(history, (h) => this.getAmountDueForEntry(h));
  });

  public bundleTransactions = computed(() => {
    const existingTransactions = this.filteredTransactions();

    const min = this.practicalMin();
    const max = this.practicalMax();

    return existingTransactions.filter((entry) =>
      Interval.fromDateTimes(min, max).contains(DateTime.fromISO(entry.buy_date as unknown as string)),
    );
  });

  public bundleTableItems = computed(() =>
    this.bundleTransactions().map((entry) => ({
      ...entry,
      bundle_name: startCase(entry.bundle_name),
      buy_date: formatDate(entry.buy_date, 'mediumDate', 'en-US'),
      amount: this.currencyPipe.transform(entry.amount_due_cents / 100, 'USD'),
    })),
  );

  public bundleTotal = computed(() => {
    const bundleTransactions = this.bundleTransactions();
    if (!bundleTransactions || bundleTransactions.length === 0) {
      return 0;
    }

    return sumBy(bundleTransactions, 'amount_due_cents') / 100;
  });

  // this *should* be 2024/3/1 but we can't select 3/1 because of mat-datepicker
  // so we subtract a month so we can get to 3/1
  public minDate = signal<Date>(new Date(2024, 2, 1));

  public readonly locationColumns: ColDef[] = [
    {
      field: 'locationName',
      headerName: 'Location Name',
      cellDataType: 'text',
      minWidth: 200,
      filter: 'agTextColumnFilter',
      sortable: true,
      sort: 'asc',
    },
    {
      field: 'priceName',
      headerName: 'Item',
    },
    {
      field: 'startDate',
      headerName: 'Start Date',
      sortable: false,
    },
    {
      field: 'endDate',
      headerName: 'End Date',
      sortable: false,
    },
    {
      field: 'unitPrice',
      headerName: 'Unit Price',
      sortable: false,
    },
    {
      field: 'usage',
      headerName: 'Usage',
      sortable: false,
    },
    {
      field: 'amount',
      headerName: 'Amount',
      sortable: false,
    },
  ];

  public readonly locationTable: GridOptions = {
    ...defaultTableDefOptions(),
  };

  public readonly hardwareColumns: ColDef[] = [
    {
      field: 'bundle_name',
      headerName: 'Item',
      cellDataType: 'text',
      minWidth: 200,
      filter: 'agTextColumnFilter',
      sortable: true,
      sort: 'asc',
    },
    {
      field: 'quantity',
      headerName: 'Quantity',
      cellDataType: 'number',
      filter: 'agNumberColumnFilter',
      sortable: true,
    },
    {
      field: 'buy_date',
      headerName: 'Purchase Date',
      sortable: false,
    },
    {
      field: 'amount',
      headerName: 'Amount',
      sortable: false,
    },
  ];

  public readonly hardwareTable: GridOptions = {
    ...defaultTableDefOptions(),
  };

  ngOnInit() {
    this.isLoading.set(true);

    forkJoin({
      branch: this.branchService.getBranchById(this.branchId()),
      bundleTransctionHistory: this.subscriptionService.getBundleTransactionHistory(this.branchId()),
      locations: this.locationService.getAllLocations({ branch_ids: [this.branchId()] }),
    }).subscribe(async ({ branch, bundleTransctionHistory, locations }) => {
      this.branch.set(branch);
      this.filteredTransactions.set(bundleTransctionHistory);
      const locationItems = locations.items;

      const customerTypes = uniq(['cfg', 'independent', 'aramark', 'canteen_one', 'canteen', this.branch().customer_type]);
      const theftPlans = customerTypes.reduce(
        (prev, cur) => ({
          ...prev,
          [cur]: this.subscriptionService.getTheftPlans(cur),
        }),
        {},
      );

      forkJoin(theftPlans).subscribe((allTheftPlans) => {
        const allPlans = Object.keys(allTheftPlans).flatMap((tp) => allTheftPlans[tp]);
        this.stripeProductPrices.set(allPlans);
      });

      if (locationItems.length === 0) {
        this.isLoading.set(false);
        return;
      }

      this.locationChangelogsService.getBranchLocationChangelogs(this.branchId()).subscribe((resp) => {
        const items = resp.items;
        const oldest = min(
          items
            .map((i) => i.changelogs)
            .flat()
            .map((res) => new Date(res.created_at)),
        );

        if (DateTime.fromJSDate(oldest) < DateTime.fromJSDate(this.minDate())) {
          this.minDate.set(DateTime.fromJSDate(oldest).toJSDate());
        }

        items.forEach((group) => {
          const location = locationItems.find((loc) => loc.id === group.location_id);
          const changelogs = group.changelogs;
          if (location.stripe_price_id || changelogs.find((item) => item.change_log.includes('stripe_price_id'))) {
            const history = this.getStripePriceChange(
              location,
              changelogs.filter((item) => item.change_log.includes('stripe_price_id')),
            );

            this.locationHistory.update((loc) => ({
              ...loc,
              [location.id]: history,
            }));

            this.filteredLocations.update((locationsList) => [...locationsList, location]);
          }
        });

        this.isLoading.set(false);
      });
    });
  }

  closeDatePickerEarly(ev) {
    this.monthSelection.set(ev.date);
    ev.datePicker['_destroyOverlay']();
  }

  getStripePriceData(priceId: string) {
    const price = this.stripeProductPrices().find((p) => p.id === priceId);
    if (price) {
      return {
        name: price.product.name.toLowerCase() ?? `Unknown Plan ${priceId}`,
        unitAmount: price.unit_amount / 100,
        interval: price.recurring.interval,
        intervalCount: price.recurring.interval_count,
      };
    }

    return {
      name: `Unsupported Plan (${priceId})`,
      unitAmount: 0,
      unsupported: true,
    };
  }

  private getMonthDaysForEntry(entry: ChangeEntry) {
    const projectedEndDate = entry.endDate ?? new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0, 23, 59, 59);
    const days = (projectedEndDate.getTime() - entry?.startDate?.getTime()) / (1000 * 3600 * 24);
    const daysOfCurrentMonth = new Date(projectedEndDate.getFullYear(), projectedEndDate.getMonth() + 1, 0).getDate();
    return { days, daysOfCurrentMonth };
  }

  private getYearDaysForEntry(entry: ChangeEntry) {
    const projectedEndDate =
      entry.endDate ?? new Date(entry.startDate.getFullYear() + 1, entry.startDate.getMonth(), entry.startDate.getDate());
    const days = (projectedEndDate.getTime() - entry?.startDate?.getTime()) / (1000 * 3600 * 24);
    const after1Year = new Date(entry.startDate.getFullYear() + 1, entry.startDate.getMonth(), entry.startDate.getDate());
    const daysOfCurrentYear = Math.ceil((after1Year.getTime() - entry.startDate.getTime()) / (1000 * 3600 * 24));

    return { days, daysOfCurrentYear };
  }

  getUsageForEntry(entry: ChangeEntry) {
    if (!entry) {
      return null;
    }

    if (!entry.startDate) {
      return null;
    }

    const priceData = this.getStripePriceData(entry.priceId);

    if (priceData.interval === 'month') {
      const { days, daysOfCurrentMonth } = this.getMonthDaysForEntry(entry);

      return `${days.toFixed(0)} days of ${daysOfCurrentMonth}`;
    }

    if (priceData.interval === 'year') {
      const { days, daysOfCurrentYear } = this.getYearDaysForEntry(entry);
      return `${days.toFixed(0)} days of ${daysOfCurrentYear}`;
    }

    return null;
  }

  getAmountDueForEntry(entry: ChangeEntry) {
    if (!entry || !entry.startDate) {
      return 0;
    }

    const priceData = this.getStripePriceData(entry.priceId);

    if (priceData.unsupported) {
      return null;
    }

    if (priceData.interval === 'month') {
      const { days, daysOfCurrentMonth } = this.getMonthDaysForEntry(entry);

      return (priceData.unitAmount / daysOfCurrentMonth) * days;
    }

    if (priceData.interval === 'year') {
      const { days, daysOfCurrentYear } = this.getYearDaysForEntry(entry);

      return (priceData.unitAmount / daysOfCurrentYear) * days;
    }

    return 0;
  }

  getStripePriceChange(location: LocationResponse, changeHistory: LocationChangelog[]) {
    const sortedChangeHistory = sortBy(changeHistory, (ch) => new Date(ch.created_at).getTime());

    const changeEntries: ChangeEntry[] = [];
    let currentPriceId = null;

    const hasPriceButNoHistory = sortedChangeHistory.length === 0 && location.stripe_price_id;
    const onlyHasSystemChangelog = sortedChangeHistory.length === 1 && sortedChangeHistory[0].actor === 'system';

    if (hasPriceButNoHistory || onlyHasSystemChangelog) {
      const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
      return [
        {
          locationId: location.id,
          locationName: location.name,
          priceId: location.stripe_price_id,
          priceBefore: null,
          startDate: monthStart,
          endDate: null,
          actor: null,
        },
      ];
    }

    sortedChangeHistory.forEach((item, index) => {
      const changeLog = JSON.parse(item.change_log);
      if (!changeLog.stripe_price_id) {
        return;
      }

      const newPriceId = changeLog.stripe_price_id;
      if (newPriceId !== currentPriceId) {
        const nextChange = sortedChangeHistory[index + 1];
        changeEntries.push({
          locationId: location.id,
          locationName: location.name,
          priceId: newPriceId,
          priceBefore: currentPriceId,
          startDate: new Date(item.created_at),
          endDate: nextChange ? new Date(nextChange.created_at) : null,
          actor: item.actor,
        });
        currentPriceId = newPriceId;
      } else if (index === sortedChangeHistory.length - 1) {
        if (item.actor === 'system' && location.stripe_price_id == changeLog.stripe_price_id) {
          changeEntries.push({
            locationId: location.id,
            locationName: location.name,
            priceId: location.stripe_price_id,
            priceBefore: null,
            startDate: item.created_at,
            endDate: null,
            actor: item.actor,
          });
        } else {
          changeEntries.push({
            locationId: location.id,
            locationName: location.name,
            priceId: currentPriceId,
            priceBefore: null,
            startDate: item.created_at,
            endDate: null,
            actor: item.actor,
          });
        }
      }
    });

    return changeEntries;
  }

  getDisplayUnitAmount(priceId: string) {
    const priceData = this.getStripePriceData(priceId);
    if (priceData.unsupported) {
      return '';
    }
    const per = priceData.intervalCount === 1 ? '/' : `/${priceData.intervalCount}`;
    return `$${priceData.unitAmount.toFixed(2)} ${per} ${priceData.interval}`;
  }

  private generateFileName(prefix: string): string {
    return `${prefix}_billing_${this.branch().name}_${DateTime.fromJSDate(this.monthSelection()).toFormat('LL-yyyy')}`;
  }

  public exportCSV(table: AgGridAngular, fileName: string) {
    table.api.exportDataAsCsv({
      fileName,
    });
  }
}
